export interface SliderOptions {
  /** CSS selector for individual slides within the container */
  slideSelector: string;
  /** Viewport width (px) below which the slider is active. Default: 768 */
  breakpoint?: number;
}

export class Slider {
  private readonly container: HTMLElement;
  private readonly slides: HTMLElement[];
  private readonly breakpoint: number;
  private readonly mediaQuery: MediaQueryList;

  private currentIndex = 0;
  private dotsWrapper: HTMLElement | null = null;
  private sliderWrapper: HTMLElement | null = null;
  private liveRegion: HTMLElement | null = null;
  private touchStartX = 0;

  constructor(container: HTMLElement, options: SliderOptions) {
    this.container = container;
    this.slides = Array.from(
      container.querySelectorAll<HTMLElement>(options.slideSelector)
    );
    this.breakpoint = options.breakpoint ?? 768;
    this.mediaQuery = window.matchMedia(`(max-width: ${this.breakpoint - 1}px)`);

    if (this.slides.length < 2) return;

    if (this.mediaQuery.matches) this.initSlider();
    this.mediaQuery.addEventListener('change', (e) =>
      e.matches ? this.initSlider() : this.destroySlider()
    );
  }

  private initSlider(): void {
    this.currentIndex = 0;

    // Wrap the track in an overflow:hidden container
    this.sliderWrapper = document.createElement('div');
    this.sliderWrapper.className = 'slider-wrapper';
    this.container.parentNode!.insertBefore(this.sliderWrapper, this.container);
    this.sliderWrapper.appendChild(this.container);

    this.container.classList.add('is-slider');
    this.updateTransform(false);
    this.updateSlideVisibility();
    this.createDots();
    this.createLiveRegion();
    this.bindTouch();
  }

  private destroySlider(): void {
    this.container.classList.remove('is-slider');
    this.container.style.transform = '';
    this.container.style.transition = '';

    // Alle Slides wieder sichtbar machen
    this.slides.forEach((slide) => slide.removeAttribute('aria-hidden'));

    // Unwrap: move container back before the wrapper, then remove wrapper
    if (this.sliderWrapper) {
      this.sliderWrapper.parentNode!.insertBefore(this.container, this.sliderWrapper);
      this.sliderWrapper.remove();
      this.sliderWrapper = null;
    }

    this.dotsWrapper?.remove();
    this.dotsWrapper = null;
    this.liveRegion?.remove();
    this.liveRegion = null;
  }

  private createDots(): void {
    this.dotsWrapper?.remove();

    this.dotsWrapper = document.createElement('div');
    this.dotsWrapper.className = 'slider-wrapper__dots';
    // Gruppe mit Label für Screenreader
    this.dotsWrapper.setAttribute('role', 'group');
    this.dotsWrapper.setAttribute('aria-label', 'Slider-Navigation');

    this.slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'slider-wrapper__dot';
      dot.setAttribute('aria-label', `Slide ${i + 1} von ${this.slides.length}`);
      // aria-current markiert den aktiven Dot
      dot.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      dot.classList.toggle('is-active', i === 0);
      dot.addEventListener('click', () => this.goTo(i));
      this.dotsWrapper!.appendChild(dot);
    });

    // Append dots inside the sliderWrapper
    (this.sliderWrapper ?? this.container).appendChild(this.dotsWrapper);
  }

  /** Versteckte Live-Region kündigt Slide-Wechsel für Screenreader an */
  private createLiveRegion(): void {
    this.liveRegion = document.createElement('div');
    this.liveRegion.className = 'sr-only';
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    (this.sliderWrapper ?? this.container).insertAdjacentElement('afterend', this.liveRegion);
  }

  private updateDots(): void {
    this.dotsWrapper
      ?.querySelectorAll<HTMLElement>('.slider-wrapper__dot')
      .forEach((dot, i) => {
        const isActive = i === this.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
  }

  /** Setzt aria-hidden auf alle nicht-sichtbaren Slides */
  private updateSlideVisibility(): void {
    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === this.currentIndex ? 'false' : 'true');
    });
  }

  private updateTransform(animate = true): void {
    this.container.style.transition = animate ? 'transform 0.35s ease' : 'none';
    this.container.style.transform = `translateX(${-this.currentIndex * 100}%)`;
  }

  /** Kündigt den Slide-Wechsel für Screenreader an */
  private announceSlide(): void {
    if (!this.liveRegion) return;
    const title = this.slides[this.currentIndex]
      .querySelector('h2, h3, h4')?.textContent?.trim() ?? '';
    this.liveRegion.textContent = title
      ? `Slide ${this.currentIndex + 1} von ${this.slides.length}: ${title}`
      : `Slide ${this.currentIndex + 1} von ${this.slides.length}`;
  }

  goTo(index: number): void {
    this.currentIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this.updateTransform();
    this.updateDots();
    this.updateSlideVisibility();
    this.announceSlide();
  }

  private bindTouch(): void {
    this.container.addEventListener(
      'touchstart',
      (e) => { this.touchStartX = e.touches[0].clientX; },
      { passive: true }
    );

    this.container.addEventListener(
      'touchend',
      (e) => {
        const diff = this.touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
          this.goTo(this.currentIndex + (diff > 0 ? 1 : -1));
        }
      },
      { passive: true }
    );
  }

  /** Initialise all sliders matching a selector on the page */
  static initAll(
    containerSelector: string,
    options: SliderOptions
  ): Slider[] {
    return Array.from(
      document.querySelectorAll<HTMLElement>(containerSelector)
    ).map((el) => new Slider(el, options));
  }
}
